import gsap from 'gsap';
import { CollectionEvents } from '../utils/events';
import { getSectionHTML } from '../utils/section-renderer';
import appStore from '../utils/state';

type FiltersModalType = 'collection' | 'search';

interface FilterRangeElement extends HTMLElement {
  reset: () => void;
}

export default class FiltersModal extends HTMLElement {
  private form: HTMLFormElement | null = null;
  private dialog: HTMLDialogElement | null = null;
  private closeButton: HTMLButtonElement | null = null;
  private submitButton: HTMLButtonElement | null = null;
  private submitButtonLabel: HTMLElement | null = null;
  private submitButtonContainer: HTMLElement | null = null;
  private clearButton: HTMLButtonElement | null = null;
  private clearButtonLabel: HTMLElement | null = null;
  private clearButtonContainer: HTMLElement | null = null;
  private filtersCountEl: HTMLElement | null = null;

  private currentProductGrid: HTMLElement | null = null;
  private newProductGrid: HTMLElement | null = null;

  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;
  private submitTl: gsap.core.Timeline | null = null;
  private showClearButtonTl: gsap.core.Timeline | null = null;
  private hideClearButtonTl: gsap.core.Timeline | null = null;
  private formChangeTl: gsap.core.Timeline | null = null;

  private unsubscribeFromFiltersModalState: (() => void) | null = null;

  private triggerElement: HTMLElement | null = null;

  private onFiltersModalToggleBound = this.onFiltersModalToggle.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);
  private onFormChangeBound = (event: Event) => this.onFormChange(event);
  private onFormSubmitBound = this.onFormSubmit.bind(this);
  private onClearButtonClickBound = this.onClearButtonClick.bind(this);

  private type: FiltersModalType = 'collection';
  private showResultsLabelTemplate: string | null = null;
  private noResultsLabelTemplate: string | null = null;
  private filtersCountTemplate: string | null = null;

  private searchParams: string | null = null;

  private isShowResultsClickable = false;
  private isClearButtonVisible: boolean | null = null;
  private filtersCount: number = 0;
  private resultsCount: number = 0;
  private filterFetchId = 0;

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.form = this.querySelector('[data-filters-modal-ref="form"]');
    this.dialog = this.querySelector('dialog');
    this.type = (this.dataset.filtersType as FiltersModalType) || 'collection';

    this.showResultsLabelTemplate = this.dataset.showResultsLabelTemplate || null;
    this.noResultsLabelTemplate = this.dataset.noResultsLabelTemplate || null;
    this.filtersCountTemplate = this.dataset.filtersCountTemplate || null;

    this.closeButton = this.querySelector('[data-filters-modal-ref="close-button"]');
    this.filtersCountEl = this.querySelector('[data-filters-modal-ref="filters-count"]');
    this.clearButton = this.querySelector('[data-filters-modal-ref="clear-button"]');
    this.clearButtonLabel = this.querySelector('[data-filters-modal-ref="clear-button-label"]');
    this.clearButtonContainer = this.querySelector('[data-filters-modal-ref="clear-button-container"]');
    this.submitButton = this.querySelector('[data-filters-modal-ref="submit-button"]');
    this.submitButtonLabel = this.querySelector('[data-filters-modal-ref="submit-button-label"]');
    this.submitButtonContainer = this.querySelector('[data-filters-modal-ref="submit-button-container"]');

    if (this.type === 'collection') {
      this.currentProductGrid = document.getElementById('collection-products-grid');
    } else {
      this.currentProductGrid = document.getElementById('search-products-grid');
    }

    this.addEventListeners();
    this.subscribe();

    if (this.form) {
      this.form.dispatchEvent(new Event('change', { bubbles: false }));
    }
  }

  addEventListeners(): void {
    if (this.form) {
      this.form.addEventListener('change', this.onFormChangeBound);
      this.form.addEventListener('submit', this.onFormSubmitBound);
    }

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('click', this.onDialogClickBound);
    }

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClearButtonClickBound);
    }
  }

  removeEventListeners(): void {
    if (this.form) {
      this.form.removeEventListener('change', this.onFormChangeBound);
      this.form.removeEventListener('submit', this.onFormSubmitBound);
    }

    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('click', this.onDialogClickBound);
    }

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.onClearButtonClickBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromFiltersModalState = appStore.subscribeKey('isFiltersModalOpen', this.onFiltersModalToggleBound);
  }

  unsubscribe(): void {
    if (this.unsubscribeFromFiltersModalState) {
      this.unsubscribeFromFiltersModalState();
      this.unsubscribeFromFiltersModalState = null;
    }
  }

  onFiltersModalToggle(isFiltersModalOpen: boolean): void {
    if (isFiltersModalOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { x: '5%' }, { x: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  hide(): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        if (this.dialog?.open) {
          this.dialog.close();
        }
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideTl.to(this.dialog, { x: '5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  private onCloseButtonClick(): void {
    appStore.actions.closeFiltersModal();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    appStore.actions.closeFiltersModal();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      appStore.actions.closeFiltersModal();
    }
  }

  onDialogClose(): void {
    if (appStore.getState().isFiltersModalOpen) {
      appStore.actions.closeFiltersModal();
    }

    this.restoreFocus();
  }

  captureTrigger(): void {
    const trigger = this.dialog?.id ? document.querySelector(`[aria-controls="${this.dialog.id}"]`) : null;

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  showClearButton() {
    if (this.hideClearButtonTl) {
      this.hideClearButtonTl.kill();
    }
    if (this.showClearButtonTl) {
      this.showClearButtonTl.kill();
    }

    this.showClearButtonTl = gsap.timeline();

    this.showClearButtonTl.to(
      this.submitButtonContainer,
      {
        willChange: 'width',
        width: `50%`,
        duration: 0.6,
        ease: 'expo.out',
        clearProps: 'willChange',
      },
      0,
    );
    this.showClearButtonTl.to(
      this.clearButtonContainer,
      {
        willChange: 'width',
        width: `50%`,
        duration: 0.6,
        ease: 'expo.out',
        clearProps: 'willChange',
      },
      0,
    );
    this.showClearButtonTl.fromTo(
      this.clearButtonLabel,
      {
        autoAlpha: 0,
      },
      { autoAlpha: 1, duration: 0.2, ease: 'power2.out' },
      0,
    );
  }

  hideClearButton() {
    if (this.showClearButtonTl) {
      this.showClearButtonTl.kill();
    }
    if (this.hideClearButtonTl) {
      this.hideClearButtonTl.kill();
    }

    this.hideClearButtonTl = gsap.timeline();

    this.hideClearButtonTl.to(
      this.submitButtonContainer,
      {
        willChange: 'width',
        width: `100%`,
        duration: 0.6,
        ease: 'expo.out',
        clearProps: 'willChange',
      },
      0,
    );
    this.hideClearButtonTl.to(
      this.clearButtonContainer,
      {
        willChange: 'width',
        width: `0%`,
        duration: 0.6,
        ease: 'expo.out',
        clearProps: 'willChange',
      },
      0,
    );
    this.hideClearButtonTl.to(
      this.clearButtonLabel,
      {
        autoAlpha: 0,
        duration: 0.2,
        ease: 'power2.out',
      },
      0,
    );
  }

  private getCollectionSectionId(): string | null {
    return document.querySelector('products-grid')?.getAttribute('section-id') ?? null;
  }

  private countActiveFilters(): number {
    if (!this.form) {
      return 0;
    }

    const standardFilters = this.form.querySelectorAll<HTMLInputElement>(
      '[data-filters-modal-ref="filters"]:checked',
    ).length;
    const sizeGroups = this.form.querySelectorAll<HTMLInputElement>(
      '[data-filters-modal-ref="size-group"]:checked',
    ).length;

    return standardFilters + sizeGroups;
  }

  private countActiveFiltersInAccordion(accordion: HTMLElement): number {
    const standardFilters = accordion.querySelectorAll<HTMLInputElement>(
      '[data-filters-modal-ref="filters"]:checked',
    ).length;
    const sizeGroups = accordion.querySelectorAll<HTMLInputElement>(
      '[data-filters-modal-ref="size-group"]:checked',
    ).length;

    return standardFilters + sizeGroups;
  }

  private formatFiltersCount(count: number): string {
    if (count <= 0 || !this.filtersCountTemplate) {
      return '';
    }

    return this.filtersCountTemplate.replace('[count]', count.toString());
  }

  private updateAccordionFiltersCounts(): void {
    this.querySelectorAll('filters-accordion').forEach((accordion) => {
      const countEl = accordion.querySelector('[data-filters-accordion-ref="filters-count"]');

      if (!countEl) {
        return;
      }

      const count = this.countActiveFiltersInAccordion(accordion as HTMLElement);
      countEl.textContent = this.formatFiltersCount(count);
    });
  }

  private updateFiltersCount(): void {
    this.filtersCount = this.countActiveFilters();

    if (this.filtersCountEl) {
      this.filtersCountEl.textContent = this.formatFiltersCount(this.filtersCount);
    }

    this.updateAccordionFiltersCounts();
  }

  private buildFilterPageUrl(): URL {
    const url = new URL(window.location.pathname, window.location.origin);

    if (this.searchParams) {
      const params = new URLSearchParams(this.searchParams);
      params.delete('page');
      url.search = params.toString();
    }

    url.hash = '';
    return url;
  }

  private parseProductGridFromDocument(doc: Document): HTMLElement | null {
    if (this.type === 'collection') {
      return doc.getElementById('collection-products-grid');
    }

    return doc.getElementById('search-products-grid');
  }

  private async fetchFilteredProductGrid(): Promise<HTMLElement | null> {
    const fetchId = ++this.filterFetchId;
    const filterUrl = this.buildFilterPageUrl();

    try {
      let responseText: string;

      if (this.type === 'collection') {
        const sectionId = this.getCollectionSectionId();

        if (!sectionId) {
          throw new Error('The section-id attribute is missing on <products-grid>');
        }

        responseText = await getSectionHTML(sectionId, filterUrl);
      } else {
        const response = await fetch(filterUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch filtered products (${response.status})`);
        }

        responseText = await response.text();
      }

      if (fetchId !== this.filterFetchId) {
        return null;
      }

      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return this.parseProductGridFromDocument(html);
    } catch (error) {
      if (fetchId === this.filterFetchId) {
        console.error(error);
      }

      return null;
    }
  }

  private updateResultsFromProductGrid(productGrid: HTMLElement | null): void {
    if (productGrid) {
      this.newProductGrid = productGrid;
    }

    const previousResultsCount = this.resultsCount;
    this.resultsCount = parseInt(productGrid?.dataset.count || '0', 10);
    this.isShowResultsClickable = this.resultsCount > 0;

    if (this.submitButton) {
      if (this.isShowResultsClickable) {
        this.submitButton.classList.remove('btn-primary-dark-disabled');
        this.submitButton.classList.add('btn-primary-dark');
      } else {
        this.submitButton.classList.remove('btn-primary-dark');
        this.submitButton.classList.add('btn-primary-dark-disabled');
      }
    }

    if (!this.submitButtonLabel || !this.showResultsLabelTemplate) {
      return;
    }

    if (previousResultsCount === this.resultsCount) {
      return;
    }

    if (this.formChangeTl) {
      this.formChangeTl.kill();
    }

    this.formChangeTl = gsap.timeline();

    this.formChangeTl.to(
      this.submitButtonLabel,
      {
        duration: 0.2,
        ease: 'power3.out',
        autoAlpha: 0,
      },
      0,
    );
    this.formChangeTl.add(() => {
      if (this.isShowResultsClickable) {
        if (this.submitButtonLabel && this.showResultsLabelTemplate) {
          this.submitButtonLabel.textContent = this.showResultsLabelTemplate.replace(
            '[count]',
            this.resultsCount.toString(),
          );
        }
      } else if (this.submitButtonLabel) {
        this.submitButtonLabel.textContent = this.noResultsLabelTemplate;
      }
    }, 0.1);
    this.formChangeTl.to(
      this.submitButtonLabel,
      {
        duration: 0.2,
        ease: 'power3.out',
        autoAlpha: 1,
      },
      0.2,
    );
  }

  private onFormChange(event: Event): void {
    event.preventDefault();

    this.isShowResultsClickable = false;
    this.newProductGrid = null;

    if (!this.form || !this.currentProductGrid) {
      return;
    }

    const formData = new FormData(this.form);
    const formKeys = [...new Set(formData.keys())];
    const hasActiveFilterParams = formKeys.some((key) => key !== 'sort_by' && key !== 'q');

    if (hasActiveFilterParams) {
      if (this.isClearButtonVisible === false || this.isClearButtonVisible === null) {
        this.isClearButtonVisible = true;
        this.showClearButton();
      }
    } else if (this.isClearButtonVisible === true || this.isClearButtonVisible === null) {
      this.isClearButtonVisible = false;
      this.hideClearButton();
    }

    const params = new URLSearchParams(formData as never);
    params.delete('page');
    this.searchParams = params.toString();

    this.updateFiltersCount();

    void this.fetchFilteredProductGrid().then((productGrid) => {
      this.updateResultsFromProductGrid(productGrid);
    });
  }

  private onFormSubmit(event: Event): void {
    event.preventDefault();

    if (!this.isShowResultsClickable || !this.currentProductGrid) {
      return;
    }

    if (this.submitTl) {
      this.submitTl.kill();
    }

    this.submitTl = gsap.timeline();

    this.submitTl.add(() => {
      this.updateUrl();
    }, 0);

    this.submitTl.add(() => {
      appStore.actions.closeFiltersModal();
    }, 0);

    this.submitTl.to(
      this.currentProductGrid,
      {
        autoAlpha: 0,
        duration: 0.3,
        ease: 'power2.out',
      },
      0.6,
    );

    this.submitTl.add(() => {
      if (this.currentProductGrid && this.newProductGrid) {
        this.currentProductGrid.replaceWith(this.newProductGrid);
        this.currentProductGrid = this.newProductGrid;
        this.newProductGrid = null;
        gsap.fromTo(this.currentProductGrid, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });

        if (this.type === 'collection') {
          document.dispatchEvent(new CustomEvent(CollectionEvents.FilterUpdate));
        }
      }
    }, 0.9);
  }

  private updateUrl(): void {
    const url = this.buildFilterPageUrl();
    history.pushState({ searchParams: this.searchParams }, '', `${url.pathname}${url.search}`);
    this.updateFiltersCount();
  }

  private onClearButtonClick(): void {
    if (!this.form) {
      return;
    }

    this.form.reset();

    const sortInputs = this.form.querySelectorAll<HTMLInputElement>('[data-filters-modal-ref="sort-options"]');
    if (sortInputs.length > 0) {
      sortInputs[0].checked = true;
    }

    const filterInputs = this.form.querySelectorAll<HTMLInputElement>('[data-filters-modal-ref="filters"]');
    filterInputs.forEach((input) => {
      input.checked = false;
    });

    this.form.querySelectorAll<HTMLInputElement>('[data-filters-modal-ref="size-group"]').forEach((input) => {
      input.checked = false;
    });

    this.form.querySelectorAll<HTMLInputElement>('[data-filter-size-group-ref="variant"]').forEach((input) => {
      input.checked = false;
    });

    this.form.querySelectorAll('filter-range').forEach((filterRange) => {
      (filterRange as FilterRangeElement).reset();
    });

    this.onFormChange(new Event('change', { bubbles: false }));
  }

  disconnectedCallback(): void {
    this.unsubscribe();
    this.removeEventListeners();
  }
}

customElements.define('filters-modal', FiltersModal);
